import {UPGRADE_PLAN_URL_NEW} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {useIsCopilotSpacesPage} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {sendEvent} from '@github-ui/hydro-analytics'
import {CommentIcon, GearIcon, ListUnorderedIcon, NoteIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Link} from '@primer/react'
import {clsx} from 'clsx'
import {useRef, useState} from 'react'

import {getPlanLabel} from '../../utils/copilot-plan'
import {useChatStateValues} from '../../utils/CopilotChatContext'
import {ManageSharedConversationsDialog} from '../conversation/ManageSharedConversationsDialog'
import {ConversationFeedbackDialog, type FeedbackDialogRef} from '../ConversationFeedbackDialog'
import {PersonalInstructionsDialog} from '../PersonalInstructionsDialog'
import styles from './UserMenu.module.css'

interface UserMenuProps {
  isCondensed?: boolean
}

export function UserMenu({isCondensed = false}: UserMenuProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const feedbackRef = useRef<FeedbackDialogRef>(null)
  const {canUpgradePlan} = useEntitlement()
  const [showManageSharedDialog, setShowManageSharedDialog] = useState(false)
  const [showPersonalInstructionsDialog, setShowPersonalInstructionsDialog] = useState(false)
  const {currentUserLogin, plan, optedInToUserFeedback} = useChatStateValues(
    'currentUserLogin',
    'plan',
    'optedInToUserFeedback',
  )
  const isSpacesPage = useIsCopilotSpacesPage()
  const planLabel = `Copilot ${getPlanLabel(plan)}`

  // Show the footer Upgrade link for eligible users when the sidebar is expanded.
  // When condensed, the overlay link (below) serves as the fallback.
  const showFooterUpgrade = canUpgradePlan && !isCondensed

  const menuOverlay = (
    <ActionMenu.Overlay width="small" align="end">
      <ActionList>
        <div className={styles.menuItem}>
          <div className={styles.userAvatar}>
            <GitHubAvatar src={`/${currentUserLogin}.png`} size={32} />
          </div>
          <div className={styles.userDetails}>
            <div className={styles.username}>{currentUserLogin}</div>
            <span className={styles.description}>
              {planLabel}
              {canUpgradePlan && isCondensed && (
                <>
                  {' · '}
                  <Link
                    onClick={() => {
                      sendEvent('dotcom_chat.activate', {
                        target: 'UPGRADE_USER_MENU_LINK',
                        mode: 'immersive',
                      })
                    }}
                    href={UPGRADE_PLAN_URL_NEW}
                    className={styles.upgradeLink}
                  >
                    Upgrade
                  </Link>
                </>
              )}
            </span>
          </div>
        </div>
        <ActionList.Divider />
        <ActionList.Item
          onSelect={() => {
            setShowPersonalInstructionsDialog(true)
            sendEvent('dotcom_chat.activate', {
              target: 'USER_MENU_PERSONAL_INSTRUCTIONS',
              mode: 'immersive',
            })
          }}
        >
          <ActionList.LeadingVisual>
            <NoteIcon />
          </ActionList.LeadingVisual>
          Personal instructions
        </ActionList.Item>
        {!isSpacesPage && (
          <ActionList.Item
            onSelect={() => {
              setShowManageSharedDialog(true)
              sendEvent('dotcom_chat.activate', {
                target: 'USER_MENU_MANAGE_SHARED',
                mode: 'immersive',
              })
            }}
          >
            <ActionList.LeadingVisual>
              <ListUnorderedIcon />
            </ActionList.LeadingVisual>
            Manage shared conversations
          </ActionList.Item>
        )}
        {optedInToUserFeedback && (
          <ActionList.Item
            onSelect={() => {
              feedbackRef.current?.openDialog()
              sendEvent('dotcom_chat.activate', {
                target: 'USER_MENU_GIVE_FEEDBACK',
                mode: 'immersive',
              })
            }}
          >
            <ActionList.LeadingVisual>
              <CommentIcon />
            </ActionList.LeadingVisual>
            Give feedback
          </ActionList.Item>
        )}
        <ActionList.LinkItem
          href="/settings/copilot"
          onClick={() =>
            sendEvent('dotcom_chat.activate', {
              target: 'USER_MENU_SETTINGS',
              mode: 'immersive',
            })
          }
        >
          <ActionList.LeadingVisual>
            <GearIcon />
          </ActionList.LeadingVisual>
          Settings
        </ActionList.LinkItem>
      </ActionList>
    </ActionMenu.Overlay>
  )

  return (
    <>
      {showFooterUpgrade ? (
        // The ActionMenu trigger button contains only the username. The avatar is a sibling
        // div outside the button, and the plan label and Upgrade link are siblings on the
        // plan row below it. A stretched ::after overlay on the button makes the full row
        // (avatar + both text lines) clickable to open the menu, while Upgrade stays an
        // independent link above the overlay via z-index.
        <div className={styles.footerAccountRow}>
          <div className={styles.userAvatar}>
            <GitHubAvatar src={`/${currentUserLogin}.png`} size={24} />
          </div>
          <div className={styles.userTextColumn}>
            <ActionMenu anchorRef={anchorRef}>
              <ActionMenu.Anchor>
                <button className={styles.userNameButton} aria-label={`${currentUserLogin}, ${planLabel}`}>
                  <div className={styles.userContent}>{currentUserLogin}</div>
                </button>
              </ActionMenu.Anchor>
              {menuOverlay}
            </ActionMenu>
            <div className={styles.planRow}>
              {planLabel}
              {' · '}
              <Link
                href={UPGRADE_PLAN_URL_NEW}
                className={styles.upgradeLink}
                onClick={() => {
                  sendEvent('dotcom_chat.activate', {
                    target: 'UPGRADE_SIDEBAR_LINK',
                    mode: 'immersive',
                  })
                }}
              >
                Upgrade
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <ActionMenu anchorRef={anchorRef}>
          <ActionMenu.Anchor>
            <button
              className={clsx(styles.userButton, isCondensed && styles.condensed)}
              aria-label={isCondensed ? 'user' : undefined}
            >
              <div className={styles.userAvatar}>
                <GitHubAvatar src={`/${currentUserLogin}.png`} size={24} />
              </div>
              <div className={styles.userContent}>
                {currentUserLogin}
                <div className={styles.planLabel}>{planLabel}</div>
              </div>
            </button>
          </ActionMenu.Anchor>
          {menuOverlay}
        </ActionMenu>
      )}

      <ConversationFeedbackDialog ref={feedbackRef} mode="immersive" returnFocusRef={anchorRef} />

      {showManageSharedDialog && (
        <ManageSharedConversationsDialog
          closeDialog={() => {
            setShowManageSharedDialog(false)
          }}
          returnFocusRef={anchorRef}
        />
      )}

      {showPersonalInstructionsDialog && (
        <PersonalInstructionsDialog
          onDismiss={() => setShowPersonalInstructionsDialog(false)}
          returnFocusRef={anchorRef}
        />
      )}
    </>
  )
}
